import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiArrowDown, FiArrowUp, FiX } from "react-icons/fi";
import { getSession } from "../utils/session";
import { API_URL, ORIGEN_API } from "../services/api";
import "./PersonalRoutineEditor.css";

// Rutina del entrenador para un cliente: sesiones (Día 1, Pierna…) con
// ejercicios del catálogo con GIF o libres. El cliente la ve en su pestaña Rutina.

const MAX_SESIONES = 7;
const MAX_EJERCICIOS = 20;
const EQUIPO = {
  "body weight": "peso corporal",
  dumbbell: "mancuernas",
  barbell: "barra",
  "leverage machine": "máquina",
  cable: "polea",
  "sled machine": "prensa",
  kettlebell: "kettlebell",
};
const OBJETIVOS = { bajar: "bajar peso", subir: "subir peso" };

const conToken = async () => ({
  headers: { Authorization: `Bearer ${(await getSession()).token}` },
});
const mensajeError = (err, porDefecto) => err.response?.data?.error || porDefecto;
const nombreDe = (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email;
const ejercicioVacio = () => ({ nombre: "", series: 3, repeticiones: "10", descanso: "60s", notas: "" });
const rutinaVacia = () => ({ nombre: "", indicaciones: "", sesiones: [{ nombre: "Día 1", ejercicios: [ejercicioVacio()] }] });
// Mueve el elemento i una posición (paso -1 o 1)
const mover = (lista, i, paso) => {
  const copia = [...lista];
  [copia[i], copia[i + paso]] = [copia[i + paso], copia[i]];
  return copia;
};

export default function PersonalRoutineEditor({ usuario, onVolver }) {
  const id = usuario._id;
  const [rutina, setRutina] = useState(null);
  const [existe, setExiste] = useState(false);
  const [catalogo, setCatalogo] = useState([]);
  const [otras, setOtras] = useState([]);
  const [sucio, setSucio] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [quitar, setQuitar] = useState(false);
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [fallo, setFallo] = useState("");

  useEffect(() => {
    (async () => {
      const cfg = await conToken();
      axios.get(`${API_URL}/personal-routines/catalogo`, cfg).then((r) => setCatalogo(r.data)).catch(() => {});
      // Clientes con rutina, para "Copiar de…"
      Promise.all([axios.get(`${API_URL}/personal-routines`, cfg), axios.get(`${API_URL}/admin/users`, cfg)])
        .then(([r, u]) => {
          const nombres = new Map(u.data.map((x) => [x._id, nombreDe(x)]));
          setOtras(r.data.filter((x) => x.userId !== id && nombres.has(x.userId)).map((x) => ({ ...x, cliente: nombres.get(x.userId) })));
        })
        .catch(() => {});
      try {
        const res = await axios.get(`${API_URL}/personal-routines/${id}`, cfg);
        setRutina(res.data);
        setExiste(true);
      } catch (err) {
        // Sin rutina se empieza una; cualquier otro fallo no deja editar,
        // para no pisar la rutina guardada con una vacía
        if (err.response?.status === 404) setRutina(rutinaVacia());
        else setFallo(mensajeError(err, "No se pudo cargar la rutina."));
      }
    })();
  }, [id]);

  const cambiar = (cambio) => {
    setRutina((r) => ({ ...r, ...cambio }));
    setSucio(true);
    setAviso("");
  };
  const cambiarSesion = (i, cambio) =>
    cambiar({ sesiones: rutina.sesiones.map((s, j) => (j === i ? { ...s, ...cambio } : s)) });
  const cambiarEjercicio = (i, k, cambio) =>
    cambiarSesion(i, { ejercicios: rutina.sesiones[i].ejercicios.map((e, j) => (j === k ? { ...e, ...cambio } : e)) });

  // Si el nombre es uno del catálogo, se enlaza (GIF e instrucciones); si no, es libre
  const enlace = (c) => ({ ejercicioId: c?.id, gif: c?.gif, tipo: c?.tipo });
  const elegirEjercicio = (i, k, nombre) =>
    cambiarEjercicio(i, k, { nombre, ...enlace(catalogo.find((x) => x.name === nombre)) });

  // Reemplaza las sesiones por otras (propuesta o copia), avisando si se pierde algo
  const reemplazar = (cambio) => {
    const hayAlgo = rutina.sesiones.some((s) => s.ejercicios.some((e) => e.nombre));
    if (hayAlgo && !window.confirm("Se reemplazarán las sesiones actuales. ¿Seguir?")) return;
    cambiar(cambio);
  };

  const proponer = async () => {
    setError("");
    try {
      const res = await axios.get(`${API_URL}/personal-routines/${id}/propuesta`, await conToken());
      const conGif = res.data.map((s) => ({
        ...s,
        ejercicios: s.ejercicios.map((e) => ({ ...e, ...enlace(catalogo.find((x) => x.id === e.ejercicioId)) })),
      }));
      reemplazar({ sesiones: conGif });
    } catch (err) {
      setError(mensajeError(err, "No se pudo generar la propuesta."));
    }
  };

  const copiarDe = async (userId) => {
    if (!userId) return;
    setError("");
    try {
      const res = await axios.get(`${API_URL}/personal-routines/${userId}`, await conToken());
      const { nombre, indicaciones, sesiones } = res.data;
      reemplazar({ nombre, indicaciones, sesiones });
    } catch (err) {
      setError(mensajeError(err, "No se pudo copiar la rutina."));
    }
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    const vacia = rutina.sesiones.find((s) => !s.ejercicios.length);
    if (vacia) return setError(`"${vacia.nombre || "Una sesión"}" no tiene ejercicios.`);
    setGuardando(true);
    try {
      const res = await axios.put(`${API_URL}/personal-routines/${id}`, rutina, await conToken());
      setRutina(res.data);
      setExiste(true);
      setSucio(false);
      setAviso(`Guardada. ${usuario.firstName || "Tu cliente"} ya la ve en su pestaña Rutina.`);
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar la rutina."));
    }
    setGuardando(false);
  };

  const quitarRutina = async () => {
    setError("");
    try {
      await axios.delete(`${API_URL}/personal-routines/${id}`, await conToken());
      setRutina(rutinaVacia());
      setExiste(false);
      setSucio(false);
      setQuitar(false);
      setAviso("Rutina quitada. El cliente vuelve a ver sólo las sesiones generadas.");
    } catch (err) {
      setError(mensajeError(err, "No se pudo quitar la rutina."));
    }
  };

  const volver = () => {
    if (!sucio || window.confirm("Hay cambios sin guardar. ¿Salir igualmente?")) onVolver();
  };

  const datosCliente = [
    OBJETIVOS[usuario.goal] && `Objetivo: ${OBJETIVOS[usuario.goal]}`,
    usuario.weight && `${usuario.weight} kg`,
    usuario.height && `${usuario.height} cm`,
  ].filter(Boolean);

  return (
    <div className="rutina-editor">
      <button type="button" className="app-btn app-btn-quiet app-btn-sm admin-volver" onClick={volver}>
        ← Volver a usuarios
      </button>

      <header className="rutina-editor-cabecera">
        <div>
          <h2>Rutina de {nombreDe(usuario)}</h2>
          <p className="rutina-editor-meta">
            {datosCliente.join(" · ") || "Sin objetivo ni medidas en su ficha"}
            {existe && rutina?.updatedAt && ` · guardada el ${new Date(rutina.updatedAt).toLocaleDateString("es-ES")}`}
          </p>
        </div>
        {rutina && (
          <div className="rutina-editor-acciones">
            <button type="button" className="app-btn app-btn-quiet app-btn-sm" onClick={proponer}>
              Generar propuesta
            </button>
            {otras.length > 0 && (
              <select
                className="app-input rutina-editor-copiar"
                value=""
                onChange={(e) => copiarDe(e.target.value)}
                aria-label="Copiar la rutina de otro cliente"
              >
                <option value="">Copiar de…</option>
                {otras.map((o) => (
                  <option key={o.userId} value={o.userId}>
                    {o.cliente}
                    {o.nombre ? ` · ${o.nombre}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
      </header>

      {fallo && <div className="app-estado app-estado-error">{fallo}</div>}
      {!rutina && !fallo && <div className="app-estado">Cargando rutina…</div>}

      {rutina && (
        <form className="rutina-editor-form" onSubmit={guardar} autoComplete="off">
          <section className="app-card rutina-editor-datos">
            <label className="rutina-campo">
              <span>Nombre de la rutina</span>
              <input
                className="app-input"
                value={rutina.nombre || ""}
                onChange={(e) => cambiar({ nombre: e.target.value })}
                placeholder="Ej.: Fuerza 3 días"
                maxLength={80}
              />
            </label>
            <label className="rutina-campo">
              <span>Indicaciones para el cliente</span>
              <textarea
                className="app-input"
                rows={3}
                value={rutina.indicaciones || ""}
                onChange={(e) => cambiar({ indicaciones: e.target.value })}
                placeholder="Ej.: Calienta 10 minutos antes. Deja un día de descanso entre sesiones."
                maxLength={2000}
              />
            </label>
          </section>

          <datalist id="rutina-catalogo">
            {catalogo.map((c) => (
              <option key={c.id} value={c.name}>
                {c.tipo} · {EQUIPO[c.equipo] || c.equipo}
              </option>
            ))}
          </datalist>

          {rutina.sesiones.map((s, i) => (
            <section key={i} className="app-card rutina-editor-sesion">
              <div className="rutina-editor-sesion-cabecera">
                <input
                  className="app-input rutina-editor-sesion-nombre"
                  value={s.nombre}
                  onChange={(e) => cambiarSesion(i, { nombre: e.target.value })}
                  aria-label={`Nombre de la sesión ${i + 1}`}
                  placeholder="Ej.: Día 1 · Pierna"
                  maxLength={80}
                  required
                />
                <button
                  type="button"
                  className="rutina-icono"
                  onClick={() => cambiar({ sesiones: mover(rutina.sesiones, i, -1) })}
                  disabled={i === 0}
                  aria-label="Subir sesión"
                  title="Subir sesión"
                >
                  <FiArrowUp aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="rutina-icono"
                  onClick={() => cambiar({ sesiones: mover(rutina.sesiones, i, 1) })}
                  disabled={i === rutina.sesiones.length - 1}
                  aria-label="Bajar sesión"
                  title="Bajar sesión"
                >
                  <FiArrowDown aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="app-btn app-btn-quiet app-btn-sm"
                  onClick={() => cambiar({ sesiones: rutina.sesiones.filter((_, j) => j !== i) })}
                  disabled={rutina.sesiones.length === 1}
                >
                  Quitar sesión
                </button>
              </div>

              <ol className="rutina-editor-ejercicios">
                {s.ejercicios.map((e, k) => (
                  <li key={k} className="rutina-editor-ejercicio">
                    <span className="rutina-editor-num" aria-hidden="true">
                      {k + 1}
                    </span>
                    {e.gif ? (
                      <img className="rutina-editor-gif" src={ORIGEN_API + e.gif} alt="" loading="lazy" />
                    ) : (
                      <span className="rutina-editor-gif rutina-editor-libre" title="Ejercicio libre, sin GIF">
                        Libre
                      </span>
                    )}
                    <div className="rutina-editor-campos">
                      <label className="rutina-campo rutina-campo-ancho">
                        <span>
                          Ejercicio {k + 1}
                          {e.tipo && <em> · {e.tipo}</em>}
                        </span>
                        <input
                          className="app-input"
                          list="rutina-catalogo"
                          value={e.nombre}
                          onChange={(ev) => elegirEjercicio(i, k, ev.target.value)}
                          placeholder="Busca en el catálogo o escribe uno libre"
                          maxLength={120}
                          required
                        />
                      </label>
                      <label className="rutina-campo">
                        <span>Series</span>
                        <input
                          type="number"
                          min="1"
                          max="20"
                          className="app-input"
                          value={e.series ?? ""}
                          onChange={(ev) => cambiarEjercicio(i, k, { series: ev.target.value === "" ? undefined : Number(ev.target.value) })}
                        />
                      </label>
                      <label className="rutina-campo">
                        <span>Repeticiones</span>
                        <input
                          className="app-input"
                          value={e.repeticiones || ""}
                          onChange={(ev) => cambiarEjercicio(i, k, { repeticiones: ev.target.value })}
                          placeholder="10, 8-12, 30 s"
                          maxLength={40}
                        />
                      </label>
                      <label className="rutina-campo">
                        <span>Descanso</span>
                        <input
                          className="app-input"
                          value={e.descanso || ""}
                          onChange={(ev) => cambiarEjercicio(i, k, { descanso: ev.target.value })}
                          placeholder="60s"
                          maxLength={40}
                        />
                      </label>
                      <label className="rutina-campo rutina-campo-ancho">
                        <span>Notas</span>
                        <input
                          className="app-input"
                          value={e.notas || ""}
                          onChange={(ev) => cambiarEjercicio(i, k, { notas: ev.target.value })}
                          placeholder="Ej.: baja en 3 segundos, RIR 2"
                          maxLength={500}
                        />
                      </label>
                    </div>
                    <div className="rutina-editor-mover">
                      <button
                        type="button"
                        className="rutina-icono"
                        onClick={() => cambiarSesion(i, { ejercicios: mover(s.ejercicios, k, -1) })}
                        disabled={k === 0}
                        aria-label={`Subir ejercicio ${k + 1}`}
                        title="Subir"
                      >
                        <FiArrowUp aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="rutina-icono"
                        onClick={() => cambiarSesion(i, { ejercicios: mover(s.ejercicios, k, 1) })}
                        disabled={k === s.ejercicios.length - 1}
                        aria-label={`Bajar ejercicio ${k + 1}`}
                        title="Bajar"
                      >
                        <FiArrowDown aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="rutina-icono rutina-icono-quitar"
                        onClick={() => cambiarSesion(i, { ejercicios: s.ejercicios.filter((_, j) => j !== k) })}
                        aria-label={`Quitar ejercicio ${k + 1}`}
                        title="Quitar"
                      >
                        <FiX aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                ))}
              </ol>

              {s.ejercicios.length < MAX_EJERCICIOS && (
                <button
                  type="button"
                  className="app-btn app-btn-quiet app-btn-sm rutina-editor-anadir"
                  onClick={() => cambiarSesion(i, { ejercicios: [...s.ejercicios, ejercicioVacio()] })}
                >
                  + Añadir ejercicio
                </button>
              )}
            </section>
          ))}

          {rutina.sesiones.length < MAX_SESIONES && (
            <button
              type="button"
              className="app-btn app-btn-quiet rutina-editor-anadir"
              onClick={() =>
                cambiar({
                  sesiones: [...rutina.sesiones, { nombre: `Día ${rutina.sesiones.length + 1}`, ejercicios: [ejercicioVacio()] }],
                })
              }
            >
              + Añadir sesión
            </button>
          )}

          <div className="rutina-editor-pie" role="status">
            {error && <div className="form-error">{error}</div>}
            {aviso && <div className="rutina-editor-aviso">{aviso}</div>}
            <div className="rutina-editor-acciones">
              <button type="submit" className="app-btn" disabled={guardando}>
                {guardando ? "Guardando…" : existe ? "Guardar cambios" : "Asignar rutina"}
              </button>
              {existe &&
                (quitar ? (
                  <button type="button" className="app-btn app-btn-danger app-btn-sm" onClick={quitarRutina}>
                    Confirmar: quitar rutina
                  </button>
                ) : (
                  <button type="button" className="app-btn app-btn-quiet app-btn-sm" onClick={() => setQuitar(true)}>
                    Quitar rutina
                  </button>
                ))}
              {sucio && <span className="rutina-editor-meta">Cambios sin guardar</span>}
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
